import { USER_ROLE } from '@skyrise-eng/types';

/**
 * A partner outside Skyrise (TapTap) who may use the Skyrise Rate Builder only.
 *
 * Defined here because the shared @skyrise-eng/types enum does not carry it
 * yet; once it does, this becomes USER_ROLE.RATE_BUILDER. The backend refuses
 * this role on every endpoint the Rate Builder does not use, so hiding the
 * rest of the app here is presentation, not the restriction itself.
 */
export const RATE_BUILDER_ROLE = 'RATE_BUILDER' as USER_ROLE;

export const USER_ROLES = [
  USER_ROLE.ADMIN,
  USER_ROLE.SUPER_ADMIN,
  USER_ROLE.ACCOUNT_MANAGER,
  USER_ROLE.CUSTOMER,
  USER_ROLE.FINANCE_MANAGER,
  USER_ROLE.LEAD_TRADER,
  USER_ROLE.TRADER,
  RATE_BUILDER_ROLE
];

export enum ACTION {
  CREATE = 'CREATE',
  READ = 'READ',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE'
}

export enum APP_SCOPE {
  MEDIA_PLAN = 'MEDIA_PLAN',
  RATE_CARD = 'RATE_CARD',
  USER_SETTINGS = 'USER_SETTINGS',
  EU6_CALCULATOR = 'EU6_CALCULATOR',
  CALCULATOR_RATE_CARD = 'CALCULATOR_RATE_CARD',
  CUSTOM_PACKAGE = 'CUSTOM_PACKAGE',
  BOOKING_SHEET = 'BOOKING_SHEET',
  TRADER_SHEET = 'TRADER_SHEET',
  CREATIVE_LIBRARY = 'CREATIVE_LIBRARY',
  DASHBOARD = 'DASHBOARD',
  REPORTS = 'REPORTS',
  FORECASTING = 'FORECASTING'
}

export const ALLOWED_ROLES: Record<APP_SCOPE, Record<ACTION, USER_ROLE[]>> = {
  [APP_SCOPE.MEDIA_PLAN]: {
    CREATE: [USER_ROLE.SUPER_ADMIN, USER_ROLE.ADMIN, USER_ROLE.FINANCE_MANAGER, USER_ROLE.ACCOUNT_MANAGER],
    READ: [
      USER_ROLE.SUPER_ADMIN,
      USER_ROLE.ADMIN,
      USER_ROLE.LEAD_TRADER,
      USER_ROLE.TRADER,
      USER_ROLE.FINANCE_MANAGER,
      USER_ROLE.ACCOUNT_MANAGER
    ],
    UPDATE: [USER_ROLE.SUPER_ADMIN, USER_ROLE.ADMIN, USER_ROLE.FINANCE_MANAGER, USER_ROLE.ACCOUNT_MANAGER],
    DELETE: [USER_ROLE.SUPER_ADMIN, USER_ROLE.ADMIN, USER_ROLE.FINANCE_MANAGER, USER_ROLE.ACCOUNT_MANAGER]
  },
  [APP_SCOPE.RATE_CARD]: {
    CREATE: [USER_ROLE.SUPER_ADMIN],
    READ: [
      USER_ROLE.SUPER_ADMIN,
      USER_ROLE.ADMIN,
      USER_ROLE.LEAD_TRADER,
      USER_ROLE.TRADER,
      USER_ROLE.FINANCE_MANAGER,
      USER_ROLE.ACCOUNT_MANAGER
    ],
    UPDATE: [USER_ROLE.SUPER_ADMIN],
    DELETE: [USER_ROLE.SUPER_ADMIN]
  },
  [APP_SCOPE.BOOKING_SHEET]: {
    CREATE: [
      USER_ROLE.SUPER_ADMIN,
      USER_ROLE.ADMIN,
      USER_ROLE.LEAD_TRADER,
      USER_ROLE.TRADER,
      USER_ROLE.FINANCE_MANAGER,
      USER_ROLE.ACCOUNT_MANAGER
    ],
    READ: [
      USER_ROLE.SUPER_ADMIN,
      USER_ROLE.ADMIN,
      USER_ROLE.LEAD_TRADER,
      USER_ROLE.TRADER,
      USER_ROLE.FINANCE_MANAGER,
      USER_ROLE.ACCOUNT_MANAGER
    ],
    UPDATE: [
      USER_ROLE.SUPER_ADMIN,
      USER_ROLE.ADMIN,
      USER_ROLE.LEAD_TRADER,
      USER_ROLE.TRADER,
      USER_ROLE.FINANCE_MANAGER,
      USER_ROLE.ACCOUNT_MANAGER
    ],
    DELETE: [USER_ROLE.SUPER_ADMIN]
  },
  [APP_SCOPE.TRADER_SHEET]: {
    CREATE: [USER_ROLE.SUPER_ADMIN, USER_ROLE.ADMIN, USER_ROLE.LEAD_TRADER, USER_ROLE.TRADER],
    READ: [USER_ROLE.SUPER_ADMIN, USER_ROLE.ADMIN, USER_ROLE.LEAD_TRADER, USER_ROLE.TRADER],
    UPDATE: [USER_ROLE.SUPER_ADMIN, USER_ROLE.LEAD_TRADER, USER_ROLE.TRADER],
    DELETE: [USER_ROLE.LEAD_TRADER, USER_ROLE.TRADER]
  },
  [APP_SCOPE.CREATIVE_LIBRARY]: {
    CREATE: [USER_ROLE.SUPER_ADMIN, USER_ROLE.ADMIN, USER_ROLE.FINANCE_MANAGER, USER_ROLE.ACCOUNT_MANAGER],
    READ: [
      USER_ROLE.SUPER_ADMIN,
      USER_ROLE.ADMIN,
      USER_ROLE.LEAD_TRADER,
      USER_ROLE.TRADER,
      USER_ROLE.FINANCE_MANAGER,
      USER_ROLE.ACCOUNT_MANAGER
    ],
    UPDATE: [USER_ROLE.SUPER_ADMIN, USER_ROLE.ADMIN, USER_ROLE.FINANCE_MANAGER, USER_ROLE.ACCOUNT_MANAGER],
    DELETE: [USER_ROLE.SUPER_ADMIN, USER_ROLE.ADMIN]
  },
  [APP_SCOPE.DASHBOARD]: {
    CREATE: [],
    READ: [
      USER_ROLE.SUPER_ADMIN,
      USER_ROLE.ADMIN,
      USER_ROLE.LEAD_TRADER,
      USER_ROLE.TRADER,
      USER_ROLE.FINANCE_MANAGER,
      USER_ROLE.ACCOUNT_MANAGER
    ],
    UPDATE: [USER_ROLE.SUPER_ADMIN, USER_ROLE.ADMIN],
    DELETE: []
  },
  [APP_SCOPE.REPORTS]: {
    CREATE: [
      USER_ROLE.SUPER_ADMIN,
      USER_ROLE.ADMIN,
      USER_ROLE.LEAD_TRADER,
      USER_ROLE.TRADER,
      USER_ROLE.FINANCE_MANAGER,
      USER_ROLE.ACCOUNT_MANAGER
    ],
    READ: [
      USER_ROLE.SUPER_ADMIN,
      USER_ROLE.ADMIN,
      USER_ROLE.LEAD_TRADER,
      USER_ROLE.TRADER,
      USER_ROLE.FINANCE_MANAGER,
      USER_ROLE.ACCOUNT_MANAGER,
      USER_ROLE.CUSTOMER
    ],
    UPDATE: [USER_ROLE.SUPER_ADMIN, USER_ROLE.LEAD_TRADER, USER_ROLE.TRADER],
    DELETE: [USER_ROLE.LEAD_TRADER, USER_ROLE.TRADER]
  },
  [APP_SCOPE.USER_SETTINGS]: {
    CREATE: [USER_ROLE.SUPER_ADMIN],
    READ: [USER_ROLE.SUPER_ADMIN],
    UPDATE: [USER_ROLE.SUPER_ADMIN],
    DELETE: [USER_ROLE.SUPER_ADMIN]
  },
  [APP_SCOPE.EU6_CALCULATOR]: {
    CREATE: [],
    READ: [
      USER_ROLE.SUPER_ADMIN,
      USER_ROLE.ADMIN,
      USER_ROLE.LEAD_TRADER,
      USER_ROLE.TRADER,
      USER_ROLE.FINANCE_MANAGER,
      USER_ROLE.ACCOUNT_MANAGER,
      RATE_BUILDER_ROLE
    ],
    UPDATE: [],
    DELETE: []
  },
  [APP_SCOPE.CALCULATOR_RATE_CARD]: {
    CREATE: [USER_ROLE.SUPER_ADMIN, USER_ROLE.ADMIN],
    READ: [
      USER_ROLE.SUPER_ADMIN,
      USER_ROLE.ADMIN,
      USER_ROLE.LEAD_TRADER,
      USER_ROLE.TRADER,
      USER_ROLE.FINANCE_MANAGER,
      USER_ROLE.ACCOUNT_MANAGER,
      RATE_BUILDER_ROLE
    ],
    UPDATE: [USER_ROLE.SUPER_ADMIN, USER_ROLE.ADMIN],
    DELETE: [USER_ROLE.SUPER_ADMIN, USER_ROLE.ADMIN]
  },
  [APP_SCOPE.FORECASTING]: {
    CREATE: [],
    READ: [
      USER_ROLE.SUPER_ADMIN,
      USER_ROLE.ADMIN,
      USER_ROLE.LEAD_TRADER,
      USER_ROLE.TRADER,
      USER_ROLE.FINANCE_MANAGER,
      USER_ROLE.ACCOUNT_MANAGER
    ],
    UPDATE: [],
    DELETE: []
  },
  [APP_SCOPE.CUSTOM_PACKAGE]: {
    CREATE: [
      USER_ROLE.SUPER_ADMIN,
      USER_ROLE.ADMIN,
      USER_ROLE.ACCOUNT_MANAGER,
      USER_ROLE.FINANCE_MANAGER,
      USER_ROLE.TRADER,
      USER_ROLE.LEAD_TRADER,
      RATE_BUILDER_ROLE
    ],
    READ: [
      USER_ROLE.SUPER_ADMIN,
      USER_ROLE.ADMIN,
      USER_ROLE.ACCOUNT_MANAGER,
      USER_ROLE.FINANCE_MANAGER,
      USER_ROLE.TRADER,
      USER_ROLE.LEAD_TRADER,
      RATE_BUILDER_ROLE
    ],
    UPDATE: [
      USER_ROLE.SUPER_ADMIN,
      USER_ROLE.ADMIN,
      USER_ROLE.ACCOUNT_MANAGER,
      USER_ROLE.FINANCE_MANAGER,
      USER_ROLE.TRADER,
      USER_ROLE.LEAD_TRADER,
      RATE_BUILDER_ROLE
    ],
    DELETE: [
      USER_ROLE.SUPER_ADMIN,
      USER_ROLE.ADMIN,
      USER_ROLE.ACCOUNT_MANAGER,
      USER_ROLE.FINANCE_MANAGER,
      USER_ROLE.TRADER,
      USER_ROLE.LEAD_TRADER,
      RATE_BUILDER_ROLE
    ]
  }
};
